import { useCallback, useRef, useState } from 'react'
import axios from 'axios'
import { initStorageUpload, uploadToS3, completeStorageUpload, abortStorageUpload } from 'services/storage'
import { extensionOf, formatBytes } from './storageUtils'

const CONCURRENCY = 3
let seq = 0

// Runs browser -> S3 uploads a few at a time and exposes their progress.
// Each upload is: ask the API for a presigned URL (creates a pending record),
// PUT the bytes to S3, then tell the API to confirm. Cancelling or failing
// after the first step aborts the pending record so it never shows up.
const useUploadQueue = ({ onUploaded, limits }) => {
  const [uploads, setUploads] = useState([])
  const queueRef = useRef([])
  const activeRef = useRef(0)
  const controllersRef = useRef({})
  const limitsRef = useRef(limits)
  limitsRef.current = limits
  const onUploadedRef = useRef(onUploaded)
  onUploadedRef.current = onUploaded

  const patch = useCallback((id, p) => {
    setUploads(list => list.map(u => (u.id === id ? { ...u, ...p } : u)))
  }, [])

  const pump = useCallback(() => {
    while (activeRef.current < CONCURRENCY && queueRef.current.length) {
      const job = queueRef.current.shift()
      if (job.controller.signal.aborted) continue
      activeRef.current += 1
      run(job) // eslint-disable-line no-use-before-define
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const run = useCallback(async (job) => {
    const { id, file, folderId, controller } = job
    let fileId = null
    try {
      const init = await initStorageUpload(file, folderId)
      if (!init || init.status >= 400) {
        patch(id, { status: 'error', error: (init && init.data && init.data.error) || 'Upload could not start' })
        return
      }
      if (controller.signal.aborted) {
        abortStorageUpload(init.data.file._id).catch(() => {})
        patch(id, { status: 'canceled' })
        return
      }
      fileId = init.data.file._id
      patch(id, { status: 'uploading', fileId })
      await uploadToS3(init.data.upload_url, file, init.data.content_type, {
        signal: controller.signal,
        onProgress: (loaded, total) => patch(id, { loaded, total })
      })
      patch(id, { status: 'finalizing' })
      const done = await completeStorageUpload(fileId)
      if (!done || done.status >= 400) {
        patch(id, { status: 'error', error: (done && done.data && done.data.error) || 'Upload could not be confirmed' })
        abortStorageUpload(fileId).catch(() => {})
        return
      }
      patch(id, { status: 'done', loaded: file.size, total: file.size })
      if (onUploadedRef.current) onUploadedRef.current(done.data, folderId)
    } catch (err) {
      if (axios.isCancel(err)) {
        patch(id, { status: 'canceled' })
      } else {
        patch(id, { status: 'error', error: (err && err.message) || 'Upload failed' })
      }
      if (fileId) abortStorageUpload(fileId).catch(() => {})
    } finally {
      activeRef.current -= 1
      delete controllersRef.current[id]
      pump()
    }
  }, [patch, pump])

  const enqueue = useCallback((files, folderId, folderName) => {
    const { maxBytes, allowed } = limitsRef.current || {}
    const items = []
    Array.from(files || []).forEach(file => {
      const id = `u${++seq}`
      const ext = extensionOf(file.name)
      let error = null
      if (!ext) error = 'File needs an extension (for example .pdf)'
      else if (allowed && allowed.length && !allowed.includes(ext)) error = `.${ext} files are not allowed`
      else if (maxBytes && file.size > maxBytes) error = `Larger than the ${formatBytes(maxBytes)} per-file limit`
      items.push({
        id, name: file.name, size: file.size, loaded: 0, total: file.size,
        status: error ? 'error' : 'queued', error, folderId, folderName
      })
      if (!error) {
        const controller = new AbortController()
        controllersRef.current[id] = controller
        queueRef.current.push({ id, file, folderId, controller })
      }
    })
    if (!items.length) return 0
    setUploads(list => [...list, ...items])
    pump()
    return items.filter(i => !i.error).length
  }, [pump])

  const cancel = useCallback((id) => {
    const controller = controllersRef.current[id]
    if (controller) controller.abort()
    // A job still waiting in the queue is skipped by pump(); mark it now so
    // the panel reflects the cancel immediately.
    patch(id, { status: 'canceled' })
  }, [patch])

  const clearFinished = useCallback(() => {
    setUploads(list => list.filter(u => ['queued', 'uploading', 'finalizing'].includes(u.status)))
  }, [])

  const activeCount = uploads.filter(u => ['queued', 'uploading', 'finalizing'].includes(u.status)).length

  return { uploads, enqueue, cancel, clearFinished, activeCount }
}

export default useUploadQueue
